import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';
import BranchCard from '../../molecules/BranchCard';
import BranchFilter from '../../molecules/BranchFilter';
import BranchMapSection from '../BranchMapSection';
import DropDownArrow from '../../atoms/Icons/DropDownArrow';
import { useDispatch, useSelector } from 'react-redux';
import useIsElementVisible from '../../../hooks/useIsElementVisible';
import BodyMedium from '../../atoms/Text/BodyMedium';
import { IRootState } from '../../../store';
import useImpressionBranchSales from '../../../hooks/emarsys/branch-sales/useImpressionBranchSales';
import { PAGE_NAME_BRANCH_SALES } from '../../../constants/emarsys/branch-sales';
import eventClickDetailBranchSales from '../../../helpers/emarsys/branch-sales/eventClickDetailBranchSales';
import LocationInfoAlert from '../../molecules/LocationInfoAlert';

const BranchFilterMobileOverlay = dynamic(
  () => import('ui/components/molecules/BranchFilterMobileOverlay'),
  {
    ssr: false,
  }
);

const BranchListSection = ({
  data = [],
  onClickFilter,
  handleClickInquiry,
  city,
  cityCode = '',
  sorted,
  title,
  description,
  branchProvince,
  breadcrumbItems,
  isProvinceReady = true,
  messageBranch = '',
}) => {
  const dispatch = useDispatch();

  const { screenSize, isLargeScreen } = useSelector((state: IRootState) => state.page);
  const { userLocation } = useSelector((state: IRootState) => state.userLocation);
  const { userData } = useSelector((state: IRootState) => state.auth) || {
    userData: null,
  };
  const headerImpressionRef = React.useRef(null);
  const branchListImpressionRef = React.useRef(null);

  const [isMapView, setIsMapView] = useState(false);
  const [filteredBranch, setFilteredBranch] = useState(data);
  const [onMobileShowAllBranch, setOnMobileShowAllBranch] = useState(false);

  const handleChangeMapView = () => {
    setIsMapView((prevValue) => !prevValue);
    eventClickEmarsys('pilih view', 'header');
  };

  useEffect(() => {
    if (isMapView) return;
    setIsMapView(isLargeScreen);
  }, [isLargeScreen]);

  useEffect(() => {
    const usedData = sorted ? data.sort((a, b) => a.distance - b.distance) : data;
    const isDesktopSizeOrMobileViewAll = !screenSize.mobile || onMobileShowAllBranch;
    const filtered = isDesktopSizeOrMobileViewAll ? usedData : usedData.slice(0, 3);
    setFilteredBranch(filtered);
  }, [screenSize, onMobileShowAllBranch, sorted, data]);

  const handleClickShowAllbranch = () => {
    setOnMobileShowAllBranch((prevValue) => !prevValue);
  };

  /* ------------------------ EVENT FOR MOBILE OVERLAY ------------------------ */
  const [scrollTop, setScrollTop] = useState(0);
  const [isFilterInView, setIsFilterInView] = useState(true);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onScroll = (e) => {
      setScrollTop(e.target.documentElement.scrollTop);
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [scrollTop]);

  /* -------------------- EVENT FOR DESKTOP FILTER DEBOUNCE ------------------- */
  const [isScrolling, setIsScrolling] = useState(false);
  const [isBranchListInView, setIsBranchListInView] = useState(true);
  const waitingTimeToShowFilter = 2000;
  const isClosingStickyFilter = isScrolling || isFilterInView || !isBranchListInView;

  useEffect(() => {
    let timeoutId;
    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsScrolling(false);
      }, waitingTimeToShowFilter);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, []);

  const eventClickEmarsys = (buttonName, sectionName, additionalNewParams = {}) => {
    eventClickDetailBranchSales({
      dispatch,
      customerNo: userData?.customerNo,
      breadcrumbItems: breadcrumbItems,
      pageName: PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA,
      pageTitle: title,
      sectionName: sectionName,
      buttonName: buttonName,
      newParams: {
        province: branchProvince?.name ?? '',
        province_code: branchProvince?.isocode ?? '',
        city: city,
        city_code: cityCode,
        ...additionalNewParams,
        ...(breadcrumbItems?.[2]?.name
          ? {
              menu_cat_lvl2: breadcrumbItems?.[2]?.name?.toUpperCase(),
            }
          : {}),
      },
    });
  };

  // For Impression Header
  useImpressionBranchSales({
    firstRef: headerImpressionRef,
    pageName: PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA,
    pageTitle: title,
    breadcrumbItems: breadcrumbItems,
    sectionName: 'header',
    isProvinceReady,
    newParams: {
      province: branchProvince?.name ?? '',
      province_code: branchProvince?.isocode ?? '',
      city: city,
      city_code: cityCode,
      ...(breadcrumbItems?.[2]?.name
        ? {
            menu_cat_lvl2: breadcrumbItems?.[2]?.name?.toUpperCase(),
          }
        : {}),
    },
  });

  // For Impression Header
  useImpressionBranchSales({
    firstRef: branchListImpressionRef,
    pageName: PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA,
    pageTitle: title,
    breadcrumbItems: breadcrumbItems,
    sectionName: 'dealer list',
    isProvinceReady,
    newParams: {
      province: branchProvince?.name ?? '',
      province_code: branchProvince?.isocode ?? '',
      city: city,
      city_code: cityCode,
      ...(breadcrumbItems?.[2]?.name
        ? {
            menu_cat_lvl2: breadcrumbItems?.[2]?.name?.toUpperCase(),
          }
        : {}),
    },
  });

  return (
    <>
      <div className="container py-5 xl:px-0" ref={headerImpressionRef}>
        <h1 className={`nama-kota text-xl md:text-2xl font-bold mb-3`}>{title}</h1>
        <p
          className="text-xs md:text-base"
          id="branch-list-desc"
          dangerouslySetInnerHTML={{ __html: description }}
        />

        {messageBranch && (
          <LocationInfoAlert>
            <p className="font-medium text-reliableBlack80 text-xs lg:text-sm">
              <span className="font-semibold">{messageBranch?.split('.')?.[0]}</span>
              {messageBranch?.split('.')?.slice(1)?.join('.')}
            </p>
          </LocationInfoAlert>
        )}
      </div>

      {/* Overlay if filter view is not visible on the screen */}
      <BranchFilterMobileOverlay
        view={!isFilterInView && isBranchListInView}
        branchCount={data.length}
        onClickFilter={onClickFilter}
        onClickHandlerViewMap={handleChangeMapView}
        city={city}
        branchProvince={branchProvince}
        pageTitle={title}
        breadcrumbItems={breadcrumbItems}
      />
      <div
        className="z-50 md:pt-5 md:pb-4 md:border-b md:bg-white"
        ref={useIsElementVisible(
          () => {
            setIsFilterInView(true);
          },
          [scrollTop],
          () => {
            setIsFilterInView(false);
          }
        )}
      >
        <BranchFilter
          branchCount={data.length}
          onClickFilter={onClickFilter}
          isMapView={isMapView}
          onClickHandlerViewMap={handleChangeMapView}
          handleClickInquiry={handleClickInquiry}
          city={city}
          branchProvince={branchProvince}
          pageTitle={title}
          breadcrumbItems={breadcrumbItems}
        />
      </div>
      <div
        className={`hidden w-screen md:block fixed ${
          isClosingStickyFilter ? '-top-[300px]' : 'md:top-[60px] lg:top-[173px]'
        } z-20 lg:pt-5 lg:pb-4 border-b bg-white transition-all`}
      >
        <BranchFilter
          branchCount={data.length}
          onClickFilter={onClickFilter}
          isMapView={isMapView}
          onClickHandlerViewMap={handleChangeMapView}
          handleClickInquiry={handleClickInquiry}
          city={city}
          branchProvince={data?.[0]?.city?.province}
          pageTitle={title}
          breadcrumbItems={breadcrumbItems}
        />
      </div>
      <div className="hidden lg:block py-4 bg-reliableBlack3 md:mb-6">
        <div className="container xl:px-0">
          <BodyMedium>
            {data.length === 0 ? (
              'Cabang Tidak Ditemukan'
            ) : (
              <>
                Menampilkan <b>{data.length}</b> Cabang di {city}
              </>
            )}
          </BodyMedium>
        </div>
      </div>
      <section
        className="border-b-4 pb-8"
        ref={useIsElementVisible(
          () => {
            setIsBranchListInView(true);
          },
          [scrollTop],
          () => {
            setIsBranchListInView(false);
          }
        )}
      >
        <div>
          {isMapView && data.length !== 0 && (
            <BranchMapSection
              onClickClose={handleChangeMapView}
              data={data}
              filteredData={filteredBranch}
              distanceData={
                userLocation.latitude
                  ? data.map((item) => ({
                      branchId: item.id,
                      distance: item.distance,
                    }))
                  : []
              }
              city={city}
              isClosingStickyFilter={isClosingStickyFilter}
              pageTitle={title}
              breadcrumbItems={breadcrumbItems}
              isProvinceReady={isProvinceReady}
              branchProvince={branchProvince}
              cityCode={cityCode}
              featureName="branchList"
              showWhatsappChat={false}
              isMapView={isMapView}
            />
          )}
          {!isMapView && (
            <div
              className="md:container xl:px-0 lg:grid grid-cols-3 gap-6"
              ref={branchListImpressionRef}
            >
              {filteredBranch.map((branch, index) => (
                <BranchCard
                  index={index}
                  key={index}
                  data={branch}
                  distance={userLocation.latitude && branch?.distance?.toLocaleString('id')}
                  city={city}
                  branchProvince={branchProvince}
                  pageTitle={title}
                  cityCode={cityCode}
                  breadcrumbItems={breadcrumbItems}
                  showWhatsappChat={false}
                  isMapView={isMapView}
                />
              ))}
            </div>
          )}

          {data.length > 3 && (
            <div className="flex justify-center lg:hidden">
              <button
                onClick={handleClickShowAllbranch}
                className="flex items-center w-fit gap-2 mt-4 cursor-pointer lg:mt-6"
              >
                <p className="text-sm font-semibold text-reliableBlack uppercase  border-b-2 border-supportiveRed lg:text-base">
                  {onMobileShowAllBranch ? 'Tampilkan Sedikit Cabang' : 'Tampilkan Semua Cabang'}
                </p>
                <span className={onMobileShowAllBranch ? 'rotate-180' : ''}>
                  <DropDownArrow size={11} />
                </span>
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default BranchListSection;
